/**
 * What each public form requires, as a schema.
 *
 * These rules used to live as a 60-line chain of `if (!formData.x.trim())` inside
 * every form component, which meant they could not be read, reused or tested
 * without rendering the page. The messages are reproduced exactly -- the point of
 * this module is to move the rules, not to change what a visitor is told.
 *
 * `fieldErrors` returns the same `{ field: message }` map the components already
 * render, so nothing about the JSX had to change.
 */
import { z } from 'zod';
import { isDomain, isEmail, isLinkedInUrl, isWebUrl } from './validators';

/** A trimmed, non-empty string. Every "X is required" rule is this. */
const required = (message: string) =>
  z
    .string()
    .transform((value) => value.trim())
    .refine((value) => value.length > 0, { message });

const email = (missing: string, malformed = 'Please enter a valid email address') =>
  required(missing).refine((value) => value === '' || isEmail(value), { message: malformed });

const agreedToTerms = (message: string) =>
  z.boolean().refine((value) => value, { message });

/** A field the form collects but never requires. */
const optional = z.string().optional().default('');

/* ------------------------------------------------------------------------- */
/* QA engineer application                                                   */
/* ------------------------------------------------------------------------- */

export const qaEngineerApplicationSchema = z
  .object({
    firstName: required('First name is required'),
    lastName: required('Last name is required'),
    email: email('Email address is required'),
    phoneNumber: required('Phone number is required'),
    countryOfResidence: required('Country of residence is required'),
    targetPosition: required('Please select your target position'),

    yearsOfExperience: required('Years of experience is required'),
    aboutYourself: required('Please tell us about yourself'),
    keySkills: required('Key skills are required'),

    jobType: required('Job type preference is required'),
    englishInterview: required('English interview preference is required'),
    expectedCompensation: required('Expected monthly salary is required'),
    agreeToTerms: agreedToTerms('You must agree to the terms and conditions to proceed'),

    linkedinProfile: z
      .string()
      .optional()
      .refine((value) => isLinkedInUrl(value ?? ''), {
        message: 'Must be a LinkedIn URL (linkedin.com domain required)',
      }),

    /*
     * The resume is one error on one field ('resume') decided by three inputs,
     * so it is checked on the object rather than on any single one of them.
     */
    resumeType: z.string(),
    resumeUrl: z.string(),
    hasUploadedFile: z.boolean(),

    // Collected, never required.
    countryCode: optional,
    currency: optional,
    telegramAccount: optional,
    companiesNotToApply: optional,

    /*
     * Admin-only. The panel can record these when it creates an application on
     * a candidate's behalf; the public form has no equivalent, which is why they
     * are optional rather than a separate schema -- the two would then have to be
     * kept in step by hand.
     */
    additionalPhoneNumber: optional,
    additionalEmail: optional,
    additionalContacts: optional,
    additionalDocumentsInformation: optional,
    credentialsComments: optional,
    credentialsAccessTable: z
      .array(
        z.object({
          serviceType: z.string().default(''),
          accessUserMail: z.string().default(''),
          accessPassword: z.string().default(''),
        })
      )
      .optional()
      .default([]),
  })
  .superRefine((value, ctx) => {
    const resumeError = (message: string) =>
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['resume'], message });

    if (value.resumeType === 'upload' && !value.hasUploadedFile) {
      resumeError('Please upload a resume file or switch to URL option');
      return;
    }
    if (value.resumeType === 'url') {
      if (!value.resumeUrl.trim()) {
        resumeError('Please provide a resume URL or switch to file upload');
      } else if (!isWebUrl(value.resumeUrl)) {
        resumeError('Please enter a valid URL (e.g., https://example.com)');
      }
    }
  });

/* ------------------------------------------------------------------------- */
/* Employer enquiry                                                          */
/* ------------------------------------------------------------------------- */

export const employerEnquirySchema = z.object({
  companyName: required('Company name is required'),
  companySize: required('Company size is required'),
  industryType: required('Industry type is required'),
  companyWebsite: required('Company website is required').refine(
    (value) => value === '' || isDomain(value),
    { message: 'Please enter a valid domain (e.g., example.com)' }
  ),

  firstName: required('First name is required'),
  lastName: required('Last name is required'),
  jobTitle: required('Job title is required'),
  email: email('Email address is required'),
  countryCode: required('Country code is required'),
  phoneNumber: required('Phone number is required'),

  positionTitle: required('Position title is required'),
  employmentType: required('Employment type is required'),
  yearsOfExperience: required('Experience level is required'),
  workArrangement: required('Work arrangement is required'),
  requiredSkills: required('Required skills are required'),
  jobDescription: z.string().trim().min(20, 'Describe what you need in at least 20 characters').max(5000, 'Keep it under 5,000 characters'),
  salaryRange: required('Monthly budget is required'),
  currency: required('Currency is required'),
  urgency: required('Urgency is required'),

  agreeToTerms: agreedToTerms('You must agree to the terms and conditions'),

  // Collected, never required.
  location: optional,
  additionalRequirements: optional,
  howDidYouHear: optional,
});

/* ------------------------------------------------------------------------- */
/* QA Talent lead (the short form on /for-employers)                          */
/* ------------------------------------------------------------------------- */

/**
 * A written description is required alongside the company, email, name and
 * role, so every enquiry tells us what the visitor actually needs.
 */
export const qaTalentLeadSchema = z.object({
  companyName: required('Company name is required'),
  email: email('Work email is required'),
  name: required('Your name is required'),
  role: required('Choose the role you are hiring for'),
  seniority: optional,
  techStack: optional,
  location: optional,
  employmentType: optional,
  startDate: optional,
  headcount: z
    .string()
    .optional()
    .default('1')
    .refine((value) => value === '' || (/^\d+$/.test(value) && Number(value) >= 1 && Number(value) <= 50), {
      message: 'Enter a number from 1 to 50',
    }),
  notes: z.string().trim().min(20, 'Describe what you need in at least 20 characters').max(2000, 'Keep it under 2,000 characters'),
  agreeToTerms: agreedToTerms('Please agree to the Terms of Service and Privacy Policy'),
});

/* ------------------------------------------------------------------------- */
/* Contact                                                                   */
/* ------------------------------------------------------------------------- */

export const contactSchema = z.object({
  name: required('Your name is required'),
  email: email('Email address is required'),
  subject: required('Subject is required'),
  message: required('Your message is required'),

  // Collected, never required. Declared because the schema is now the single
  // description of the form's fields, not just of its rules.
  company: optional,
  role: optional,
});

/* ------------------------------------------------------------------------- */
/* Booking brief                                                             */
/* ------------------------------------------------------------------------- */

/** The visitor describes the enquiry themselves; no preset request or NDA flag. */
export const briefSchema = z.object({
  name: required('Your name is required'),
  email: email('Work email is required'),
  company: required('Company is required'),
  message: z.string().trim().min(20, 'Describe what you need in at least 20 characters').max(5000, 'Keep it under 5,000 characters'),
});

/* ------------------------------------------------------------------------- */

/**
 * The first error per field, keyed by field name.
 *
 * One message per field, because that is what each form renders under its
 * input -- and it is the order the old `if` chains produced, since the first
 * matching branch won.
 */
export function fieldErrors(error: z.ZodError): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const issue of error.issues) {
    const field = issue.path.join('.');
    if (field && !(field in errors)) {
      errors[field] = issue.message;
    }
  }
  return errors;
}

/** Validates and returns the errors to render. Empty means the form is good. */
export function validate<T extends z.ZodTypeAny>(
  schema: T,
  value: unknown
): Record<string, string> {
  const result = schema.safeParse(value);
  return result.success ? {} : fieldErrors(result.error);
}

/*
 * The value types each form works with.
 *
 * `input` rather than `output` because the required() fields trim, so what a
 * component holds while typing is not what the schema produces -- react-hook-form
 * needs the former for its field values and hands you the latter on submit.
 */
export type BriefValues = z.input<typeof briefSchema>
export type ContactValues = z.input<typeof contactSchema>
export type EmployerEnquiryValues = z.input<typeof employerEnquirySchema>
export type QaTalentLeadValues = z.input<typeof qaTalentLeadSchema>
export type QAEngineerApplicationValues = z.input<typeof qaEngineerApplicationSchema>
